import { ApolloClient, ApolloLink, HttpLink, InMemoryCache } from '@apollo/client';
import { setContext } from '@apollo/client/link/context';
import { useNewVersionAvailability } from 'hooks/useNewVersionAvailability';
import { VERSION } from '../../environments/version';
import { getToken, removeToken } from './token';

export const httpLink = new HttpLink({
  uri: process.env.NX_API_BASE_URL,
});

/**
 * Middleware for setting the authorization header
 */
const authLink = setContext((_, { headers }) => {
  // return the headers to the context so httpLink can read them
  const { tokenType, accessToken } = getToken();
  return {
    headers: {
      ...headers,
      authorization: `${tokenType ?? ''} ${accessToken ?? ''}`,
    },
  };
});

const versionLink = new ApolloLink((operation, forward) => {
  return forward(operation).map((response) => {
    // check for 401 unauthorized errors
    if (response.errors?.filter((error) => error.extensions?.response?.statusCode === 401).length) {
      removeToken();
    }
    const version = operation.getContext().response.headers.get('X-DC-VERSION');
    if (version && VERSION && version !== VERSION) {
      useNewVersionAvailability.setState({ isNewVersionAvailable: version });
    }
    return response;
  });
});

const cleanTypeName = new ApolloLink((operation, forward) => {
  if (operation.variables) {
    const omitTypename = (key: unknown, value: unknown) =>
      key === '__typename' ? undefined : value;
    operation.variables = JSON.parse(JSON.stringify(operation.variables), omitTypename);
  }
  return forward(operation).map((data) => {
    return data;
  });
});

const apolloClient = new ApolloClient<unknown>({
  link: ApolloLink.from([authLink, versionLink, cleanTypeName, httpLink]), // order of middlewares is important! make sure httpLinks comes last so the modified request will be sent
  // TODO: figure out if this is even needed while we're currently suppressing all caching
  // cache: new InMemoryCache({ addTypename: false }),
  cache: new InMemoryCache(),
  defaultOptions: {
    query: {
      fetchPolicy: 'no-cache',
    },
    mutate: {
      fetchPolicy: 'no-cache',
    },
    watchQuery: {
      fetchPolicy: 'no-cache',
    },
  },
});

export default apolloClient;
